import { useEffect, useCallback } from 'react';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MetaPixel - Loads Facebook/Meta Pixel ONLY when user has accepted ALL cookies.
 * Fetches pixel ID from /api/settings.
 * Tracks: PageView, ViewContent, and exposes fbq for custom events.
 */
const MetaPixel = () => {
  const loadPixel = useCallback((pixelId) => {
    if (!pixelId || window.fbq) return;

    // Meta Pixel base code
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
  }, []);

  useEffect(() => {
    // Check if user accepted all cookies
    const consentStr = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consentStr) return;

    try {
      const consent = JSON.parse(consentStr);
      if (!consent.marketing) return; // Only load if marketing cookies accepted
    } catch {
      return;
    }

    // Fetch pixel ID from settings
    fetch(`${API_URL}/api/settings`)
      .then(r => r.json())
      .then(data => {
        if (data.metaPixelId) {
          loadPixel(data.metaPixelId);
        }
      })
      .catch(() => {});
  }, [loadPixel]);

  // Listen for cookie consent changes (when user clicks "Aceptar todas")
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === COOKIE_CONSENT_KEY && e.newValue) {
        try {
          const consent = JSON.parse(e.newValue);
          if (consent.marketing) {
            fetch(`${API_URL}/api/settings`)
              .then(r => r.json())
              .then(data => {
                if (data.metaPixelId) loadPixel(data.metaPixelId);
              })
              .catch(() => {});
          }
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [loadPixel]);

  return null; // No visual output
};

/**
 * Track a Meta Pixel event. Call this from anywhere.
 * Only fires if fbq is loaded (user accepted all cookies + pixel ID configured).
 */
export const trackEvent = (eventName, params = {}) => {
  if (window.fbq) {
    window.fbq('track', eventName, params);
  }
};

/**
 * Track a custom Meta Pixel event.
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
};

export default MetaPixel;
